import { AxiosRequestConfig } from 'axios';
import { BasePaginatorDto } from '@diritto/common/base/paginator/paginator.dto';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import {
  ContentsRelayRequestDto,
  ContentsRelayResponseDto,
  WsContentRelayResponseDto,
} from '@diritto/common/relay/dtos/content-relay.dtos';
import {
  ContentEpisodesRelayResponseDto,
  EpisodeRelayRequestDto,
  EpisodesRelayResponseDto,
  EpisodeViewRelayResponseDto,
  WsContentEpisodesRequestDto,
} from '@diritto/common/relay/dtos/episode-relay.dtos';
import { FancyShareRelayResponseDto } from '@diritto/common/relay/dtos/fancy-share-relay.dtos';
import {
  ProfilesLiteRelayRequestDto,
  ProfilesLiteRelayResponseDto,
} from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ProfileRelayRequestDto, ProfileRelayResponseDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import {
  SectionContentRelayRequestDto,
  SectionContentRelayResponseDto,
  SectionRelayPersonalResponseDto,
  SectionRelayRequestDto,
  SectionRelayResponseDto,
} from '@diritto/common/relay/dtos/section-relay.dtos';
import { Http } from '@diritto/common/utils/requester/http';
import { generateUrl } from '@diritto/common/utils/urls';

export class RelayApi {
  constructor(
    protected url: string,
    protected http: Http,
  ) {}

  async sections(sectionRelayRequestDto: SectionRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.get<SectionRelayResponseDto>(
      generateUrl(`${this.url}/relay/sections`, sectionRelayRequestDto),
      options,
    );
    return res.data;
  }

  async sectionsPersonal(includeAdult: boolean, position: SectionPosition, options?: AxiosRequestConfig) {
    const res = await this.http.get<SectionRelayPersonalResponseDto>(
      generateUrl(`${this.url}/relay/sections/personal`, { includeAdult, position }),
      options,
    );
    return res.data;
  }

  async sectionsContent(
    slug: string,
    data: SectionContentRelayRequestDto,
    paginator?: BasePaginatorDto,
    options?: AxiosRequestConfig,
  ) {
    const res = await this.http.get<SectionContentRelayResponseDto>(
      generateUrl(`${this.url}/relay/sections/${slug}`, { ...data, ...paginator }),
      options,
    );

    return res.data;
  }

  async contents(contentRelayRequestDto: ContentsRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.post<ContentsRelayRequestDto, ContentsRelayResponseDto>(
      `${this.url}/relay/contents`,
      contentRelayRequestDto,
      options,
    );
    return res.data;
  }

  async workspaceContent(contentId: number, options?: AxiosRequestConfig) {
    const res = await this.http.get<WsContentRelayResponseDto>(
      `${this.url}/relay/workspace/contents/${contentId}`,
      options,
    );
    return res.data;
  }

  async workspaceContents(contentRelayRequestDto: ContentsRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.post<ContentsRelayRequestDto, ContentsRelayResponseDto>(
      `${this.url}/relay/workspace/contents`,
      contentRelayRequestDto,
      options,
    );
    return res.data;
  }

  async workspaceContentEpisodes(
    contentId: number,
    data: WsContentEpisodesRequestDto,
    paginator: BasePaginatorDto,
    options?: AxiosRequestConfig,
  ) {
    const res = await this.http.get<ContentEpisodesRelayResponseDto>(
      `${this.url}/relay/workspace/contents/${contentId}/episodes`,
      {
        params: { ...data, ...paginator },
        ...options,
      },
    );
    return res.data;
  }

  async episodes(episodeRelayRequestDto: EpisodeRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.post<EpisodeRelayRequestDto, EpisodesRelayResponseDto>(
      `${this.url}/relay/episodes`,
      episodeRelayRequestDto,
      options,
    );
    return res.data;
  }

  async episodeView(episodeId: number, options?: AxiosRequestConfig) {
    const res = await this.http.get<EpisodeViewRelayResponseDto>(
      `${this.url}/relay/episodes/${episodeId}/view`,
      options,
    );
    return res.data;
  }

  async profiles(profileRelayRequestDto: ProfileRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.post<ProfileRelayRequestDto, ProfileRelayResponseDto>(
      `${this.url}/relay/profiles`,
      profileRelayRequestDto,
      options,
    );
    return res.data;
  }

  async profilesLite(profilesLiteRelayRequestDto: ProfilesLiteRelayRequestDto, options?: AxiosRequestConfig) {
    const res = await this.http.post<ProfilesLiteRelayRequestDto, ProfilesLiteRelayResponseDto>(
      `${this.url}/relay/profiles-lite`,
      profilesLiteRelayRequestDto,
      options,
    );
    return res.data;
  }

  async fancyShare(fancyShareId: number, options?: AxiosRequestConfig) {
    const res = await this.http.get<FancyShareRelayResponseDto>(
      `${this.url}/relay/fancy-shares/${fancyShareId}`,
      options,
    );
    return res.data;
  }
}
